import { Injectable } from '@angular/core';
import { HttpClient, HttpContext, HttpHeaders } from '@angular/common/http';
import { Observable } from 'rxjs';
import { environment } from 'src/environments/environment';
import { StorageService } from './storage.service';
import { BACKGROUND_REQUEST, SURFACE_PERMISSION_ERRORS } from './http-context-tokens';

@Injectable({
  providedIn: 'root'
})
export class ApiRequestService {

  constructor(
    private http: HttpClient,
    private storage: StorageService
  ) { }

  private prepareRequestURL(endpoint: string): string {
    let url = '';
    // Check if the endpoint starts with http/https
    if (!/^https?:\/\//i.test(endpoint)) {
      // Get the api url and append the endpoint
      url = environment.API_URL + endpoint;
    } else {
      // The endpoint contains http/s. Do not make changes.
      url = endpoint;
    }
    return url;
  }

  /**
   * @param surfacePermissionErrors Set to true for a USER INITIATED GET (an export, a download,
   *                                opening a record) so a permission denial is shown to the user
   *                                instead of failing silently. Background GET loads should leave
   *                                this false and render their own inline no-permission state.
   *                                Ignored for non-GET requests, which always surface denials.
   * @param backgroundRequest       Set to true for a request the user did not trigger (a poll, a focus
   *                                validation, a badge refresh) so a failure to reach the server is
   *                                never shown to them. The error is still rethrown.
   */
  makeRequestObservable(
    method: string,
    endpoint: string,
    payload: any = {},
    params = {},
    headers = {},
    surfacePermissionErrors: boolean = false,
    backgroundRequest: boolean = false
  ): Observable<any> {

    // Add the authorization header if it is not present.
    if ( typeof headers['Authorization'] == 'undefined' ) {
      headers['Authorization'] = 'Bearer ' + this.storage.get('AuthToken');
    }

    let request: any;

    const options = {
      headers: new HttpHeaders(headers),
      params: params,
      context: new HttpContext()
        .set(SURFACE_PERMISSION_ERRORS, surfacePermissionErrors)
        .set(BACKGROUND_REQUEST, backgroundRequest)
    };

    const apiEndpoint = this.prepareRequestURL(endpoint);

    switch (method) {
      case 'post':
      case 'POST':
        request = this.http.post(apiEndpoint, payload, options);
        break;
      case 'put':
      case 'PUT':
        request = this.http.put(apiEndpoint, payload, options);
        break;
      case 'delete':
      case 'DELETE':
        request = this.http.delete(apiEndpoint, options);
        break;
      case 'patch':
      case 'PATCH':
        request = this.http.patch(apiEndpoint, payload, options);
        break;
      default:
        request = this.http.get(apiEndpoint, options);
    }

    return request;
  }

  makeRequest(
    method: string,
    endpoint: string,
    payload: any = {},
    params = {},
    headers = {},
    surfacePermissionErrors: boolean = false,
    backgroundRequest: boolean = false
  ): Promise<any> {
    return this.makeRequestObservable(method, endpoint, payload, params, headers, surfacePermissionErrors, backgroundRequest).toPromise();
  }

  makeUploadRequest(
    endpoint: string,
    files: File | File[],
    params = {},
    headers = {}
  ) {
    const form = new FormData();
    if ( Array.isArray(files) ) {
      files.forEach((file) => {
        form.append('files[]', file, file.name);
      });
    } else {
      form.append('file', files, files.name);
    }
    return this.makeRequest('post', endpoint, form, params, headers);
  }

  // Get file blobs from an API request.
  makeDownloadRequest(
    endpoint: string,
    headers = {},
    params = {}
  ): Promise<any> {

    if ( typeof headers['Authorization'] == 'undefined' ) {
      headers['Authorization'] = 'Bearer ' + this.storage.get('AuthToken');
    }

    headers['Content-Type'] = 'application/json';
    headers['Accept'] = 'application/json';

    const apiEndpoint = this.prepareRequestURL(endpoint);

    return this.http.get(apiEndpoint, {
      headers: new HttpHeaders(headers),
      responseType: 'blob', // Response must be blob
      params: params,
      // A download is always user initiated, so a permission denial must be surfaced rather than
      // failing silently.
      context: new HttpContext().set(SURFACE_PERMISSION_ERRORS, true)
    }).toPromise();
  }


    /**
     * Makes an unauthenticated HTTP request to an external API.
     * Does NOT attach Authorization headers or prepend the SiteConnect API base URL.
     * Use this for third-party APIs (e.g., IP geolocation, external services).
     */
    makeExternalRequestObservable(
      method: string,
      url: string,
      payload: any = {},
      params = {},
      headers = {}
    ): Observable<any> {
      let request: any;

      const options = {
        headers: new HttpHeaders(headers),
        params: params
      };

      switch (method) {
        case 'post':
        case 'POST':
          request = this.http.post(url, payload, options);
          break;
        case 'put':
        case 'PUT':
          request = this.http.put(url, payload, options);
          break;
        case 'delete':
        case 'DELETE':
          request = this.http.delete(url, options);
          break;
        case 'patch':
        case 'PATCH':
          request = this.http.patch(url, payload, options);
          break;
        default:
          request = this.http.get(url, options);
      }

      return request;
    }

}
